import {lshGETAUTH} from './core';
import {graphqlRequestHandler} from './core';
import {
  IGroup,
  IGroupMembersResponse,
  IGroupRoles,
  IGroupActionResponse,
  IGroupInvitationCode,
  ICreateGroupInput,
  ICreateGroupResponse,
  IGroupMessage,
} from 'constants/interfaces';

/**
 * Fetch all groups visible to admin
 */
export async function apiAdminGetGroups(): Promise<IGroup[]> {
  return new Promise((resolve, reject) => {
    lshGETAUTH().then((token) => {
      const headers = {Authorization: token};
      const query = `
        query Groups {
          groups {
            groupId
            name
            description
            isPrivate
            createdAt
            admins {
              userId
              username
            }
          }
        }
      `;
      graphqlRequestHandler('groups', query, {}, headers)
        .then((res) => resolve(res as IGroup[]))
        .catch((error) => reject(error));
    });
  });
}

/**
 * Fetch paginated member list for a group
 * @param {string} groupId
 * @param {number} page
 * @param {number} limit
 */
export async function apiAdminGetGroupMembers(
  groupId: string,
  page = 1,
  limit = 20,
): Promise<IGroupMembersResponse> {
  return new Promise((resolve, reject) => {
    lshGETAUTH().then((token) => {
      const headers = {Authorization: token};
      const query = `
        query AdminGetGroupMembers($groupId: String!, $page: Int!, $limit: Int!) {
          adminGetGroupMembers(input: { groupId: $groupId, page: $page, limit: $limit }) {
            members {
              userId
              username
              avatar
              phone
              isOnline
            }
            total
            page
            limit
            totalPages
            hasNextPage
            hasPreviousPage
          }
        }
      `;
      graphqlRequestHandler(
        'adminGetGroupMembers',
        query,
        {groupId, page, limit},
        headers,
      )
        .then((res) => resolve(res as IGroupMembersResponse))
        .catch((error) => reject(error));
    });
  });
}

/**
 * Fetch roles (owner / admins / members) for a group
 * @param {string} groupId
 */
export async function apiAdminGetGroupRoles(
  groupId: string,
): Promise<IGroupRoles> {
  return new Promise((resolve, reject) => {
    lshGETAUTH().then((token) => {
      const headers = {Authorization: token};
      const query = `
        query AdminGetGroupRoles($groupId: String!) {
          adminGetGroupRoles(groupId: $groupId) {
            groupId
            groupName
            owner { userId username isOwner }
            admins { userId username isOwner }
            members { userId username isOwner }
          }
        }
      `;
      graphqlRequestHandler('adminGetGroupRoles', query, {groupId}, headers)
        .then((res) => resolve(res as IGroupRoles))
        .catch((error) => reject(error));
    });
  });
}

/**
 * Add a farmer as a member of a group
 * @param {string} groupId
 * @param {number} farmerId
 */
export async function apiAdminAddGroupMember(
  groupId: string,
  farmerId: number,
): Promise<IGroupActionResponse> {
  return new Promise((resolve, reject) => {
    lshGETAUTH().then((token) => {
      const headers = {Authorization: token};
      const query = `
        mutation AdminAddGroupMember($groupId: String!, $farmerId: Int!) {
          adminAddGroupMember(input: { groupId: $groupId, farmerId: $farmerId }) {
            success
            message
          }
        }
      `;
      graphqlRequestHandler(
        'adminAddGroupMember',
        query,
        {groupId, farmerId},
        headers,
      )
        .then((res) => resolve(res as IGroupActionResponse))
        .catch((error) => reject(error));
    });
  });
}

/**
 * Grant admin role to a member in a group.
 * Uses adminAddGroupMember — the actual mutation the backend exposes for this action.
 * @param {string} groupId
 * @param {number} userId
 */
export async function apiAdminGrantAdmin(
  groupId: string,
  userId: number,
): Promise<IGroupActionResponse> {
  return new Promise((resolve, reject) => {
    lshGETAUTH().then((token) => {
      const headers = {Authorization: token};
      const query = `
        mutation AdminAddGroupMember($groupId: String!, $farmerId: Int!) {
          adminAddGroupMember(input: { groupId: $groupId, farmerId: $farmerId }) {
            success
            message
          }
        }
      `;
      graphqlRequestHandler(
        'adminAddGroupMember',
        query,
        {groupId, farmerId: userId},
        headers,
      )
        .then((res) => resolve(res as IGroupActionResponse))
        .catch((error) => reject(error));
    });
  });
}

/**
 * Revoke admin role from a user in a group
 * @param {string} groupId
 * @param {number} userId
 */
export async function apiAdminRevokeAdmin(
  groupId: string,
  userId: number,
): Promise<IGroupActionResponse> {
  return new Promise((resolve, reject) => {
    lshGETAUTH().then((token) => {
      const headers = {Authorization: token};
      const query = `
        mutation AdminRevokeAdmin($groupId: String!, $userId: Int!) {
          adminRevokeAdmin(input: { groupId: $groupId, userId: $userId }) {
            success
            message
          }
        }
      `;
      graphqlRequestHandler(
        'adminRevokeAdmin',
        query,
        {groupId, userId},
        headers,
      )
        .then((res) => resolve(res as IGroupActionResponse))
        .catch((error) => reject(error));
    });
  });
}

/**
 * Generate an invitation code for a group
 * @param {string} groupId
 */
export async function apiGenerateGroupInvitationCode(
  groupId: string,
): Promise<IGroupInvitationCode> {
  return new Promise((resolve, reject) => {
    lshGETAUTH().then((token) => {
      const headers = {Authorization: token};
      const query = `
        mutation GenerateGroupInvitationCode($groupId: String!) {
          generateGroupInvitationCode(groupId: $groupId) {
            invitationCode
            inviteCodeExpiryDate
          }
        }
      `;
      graphqlRequestHandler(
        'generateGroupInvitationCode',
        query,
        {groupId},
        headers,
      )
        .then((res) => resolve(res as IGroupInvitationCode))
        .catch((error) => reject(error));
    });
  });
}

/**
 * Create a new group
 * @param {any} input
 */
export async function apiCreateGroup(
  input: ICreateGroupInput,
): Promise<ICreateGroupResponse> {
  return new Promise((resolve, reject) => {
    lshGETAUTH().then((token) => {
      const headers = {Authorization: token};
      const query = `
        mutation CreateGroup($createGroupInput: CreateGroupInput!) {
          createGroup(createGroupInput: $createGroupInput) {
            groupId
            name
            description
            isPrivate
            members {
              userId
              username
            }
            admins {
              userId
              username
            }
            createdBy {
              userId
              username
            }
          }
        }
      `;
      graphqlRequestHandler(
        'createGroup',
        query,
        {createGroupInput: input},
        headers,
      )
        .then((res) => resolve(res as ICreateGroupResponse))
        .catch((error) => reject(error));
    });
  });
}

/**
 * Fetch messages for a group
 * @param {string} groupId
 */
export async function apiGetGroupMessages(
  groupId: string,
): Promise<IGroupMessage[]> {
  return new Promise((resolve, reject) => {
    lshGETAUTH().then((token) => {
      const headers = {Authorization: token};
      const query = `
        query GetGroupMessages($groupId: String!) {
          messages(groupId: $groupId) {
            uuid
            content
            type
            isEdited
            isDeleted
            createdAt
            sender {
              userId
              username
              avatar
            }
          }
        }
      `;
      graphqlRequestHandler('messages', query, {groupId}, headers)
        .then((res) => resolve(res as IGroupMessage[]))
        .catch((error) => reject(error));
    });
  });
}
